'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { submitContact } from '@/lib/actions/public';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';

export function ContactForm({ locale, dict }: { locale: Locale; dict: Dictionary }) {
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setLoading(true);
    const result = await submitContact({
      full_name: String(fd.get('full_name') ?? ''),
      email: String(fd.get('email') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      subject: String(fd.get('subject') ?? ''),
      message: String(fd.get('message') ?? ''),
      locale,
    });
    setLoading(false);
    if (result.ok) {
      toast.success(dict.contact.success);
      form.reset();
    } else {
      toast.error(dict.contact.error);
    }
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={dict.contact.name} name="full_name" autoComplete="name" required />
        <Field label={dict.contact.email} name="email" type="email" autoComplete="email" required />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={dict.contact.phone} name="phone" autoComplete="tel" />
        <Field label={dict.contact.subject} name="subject" />
      </div>
      <div>
        <Label htmlFor="message" className="mb-1.5 block text-sm font-medium">{dict.contact.message}</Label>
        <Textarea id="message" name="message" rows={5} required className="resize-none" />
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <Send className="mr-2 h-4 w-4" />
        {loading ? dict.common.loading : dict.contact.submit}
      </Button>
    </form>
  );
}

function Field({ label, name, type = 'text', required = false, autoComplete }: { label: string; name: string; type?: string; required?: boolean; autoComplete?: string }) {
  return (
    <div>
      <Label htmlFor={name} className="mb-1.5 block text-sm font-medium">{label}{required && <span className="text-accent"> *</span>}</Label>
      <Input id={name} name={name} type={type} required={required} autoComplete={autoComplete} />
    </div>
  );
}
